/**
 * History Item Actions Component
 * Dropdown menu with ellipsis icon for history item actions
 * Uses ActionMenu component (built on shadcn dropdown menu)
 * Follows SRP - single responsibility for action menu
 * Shows "Share" option when provided
 */

import React from 'react';
import { ActionMenu, ActionMenuItem } from '@/components/ui/ActionMenu';

interface HistoryItemActionsProps {
  onShare?: () => void;
  onDelete?: () => void;
}

/**
 * Ellipsis Icon Component
 * Using img tag for SVG icons (best practice - simpler and no optimization overhead)
 */
const EllipsisIcon: React.FC = () => {
  return (
    /* eslint-disable-next-line @next/next/no-img-element -- SVG icon */
    <img
      src='/assets/images/icons/ellipsis.svg'
      alt='More options'
      width={24}
      height={24}
      style={{
        display: 'block',
        objectFit: 'contain',
      }}
      onError={e => {
        // Silently handle icon loading errors to prevent breaking the UI
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

export const HistoryItemActions: React.FC<HistoryItemActionsProps> = ({
  onShare,
  onDelete,
}) => {
  // Build menu items
  const menuItems: ActionMenuItem[] = [
    ...(onShare
      ? [
          {
            label: 'Share',
            onClick: onShare,
            iconSrc: '/assets/images/icons/share.svg',
          } as ActionMenuItem,
        ]
      : []),
    ...(onDelete
      ? [
          {
            label: 'Delete',
            onClick: onDelete,
            iconSrc: '/assets/images/icons/trash.svg',
          } as ActionMenuItem,
        ]
      : []),
  ];

  if (menuItems.length === 0) {
    return null;
  }

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <ActionMenu
      items={menuItems}
      trigger={<EllipsisIcon />}
      align='end'
      onTriggerClick={handleTriggerClick}
    />
  );
};
